import React from 'react';
import { brand } from './utils/brand';

export const fontSize = {
  xs: '16px',
  sm: '22px',
  base: '26px',
  lg: '32px',
  xl: '40px',
  '2xl': '52px',
  '3xl': '64px'
};

export const lineHeight = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2
};

export const whiteSpace = {
  '0': '0',
  '1': '0.25rem',
  '2': '0.5rem',
  '3': '0.75rem',
  '4': '1rem',
  '5': '1.25rem',
  '6': '1.5rem',
  '8': '2rem',
  '10': '2.5rem',
  '12': '3rem',
  '14': '3.5rem',
  '16': '4rem',
  '20': '5rem',
  '22': '5.5rem',
  '24': '6rem',
  '32': '8rem',
  '40': '10rem',
  '48': '12rem',
  '56': '14rem',
  '64': '16rem'
};

const COLOR_KEYS = [
  'cta',
  'cta200',
  'cta300',
  'cta400',
  'cta500',
  'cta600',
  'ui100',
  'ui200',
  'ui300',
  'ui400',
  'ui500',
  'ui600',
  'ui700',
  'ui800',
  'ui900',
  'error',
  'error300',
  'warning',
  'success',
  'info',
  'white',
  'black',
  'imdb',
  'facebook',
  'backdrop'
] as const;

export type ColorKey = typeof COLOR_KEYS[number];

export type Colors = { [K in ColorKey]: K };
export const Colors: Colors = COLOR_KEYS.reduce(
  (acc, k) => ({ ...acc, [k]: k }),
  {} as Colors
);

type RGB = [number, number, number];

// rgb() triplets extracted from
// node_modules/@sfstudios/shapeshifter/build/legacy.css
// We do NOT import that CSS at runtime: Tizen 2.4 / 3.0 (Chrome 47/56) cannot
// parse `rgb(R G B / A)`, so themeColor() returns hex/rgba directly here.
const PALETTES: Record<'SFAnytime' | 'Blockbuster', Record<ColorKey, RGB>> = {
  SFAnytime: {
    cta: [241, 137, 0],
    cta200: [249, 207, 153],
    cta300: [247, 184, 102],
    cta400: [244, 161, 51],
    cta500: [241, 137, 0],
    cta600: [216, 123, 0],
    ui100: [229, 229, 229],
    ui200: [216, 216, 216],
    ui300: [156, 156, 156],
    ui400: [90, 90, 90],
    ui500: [46, 46, 46],
    ui600: [36, 36, 36],
    ui700: [29, 29, 29],
    ui800: [23, 23, 23],
    ui900: [13, 13, 13],
    error: [235, 51, 56],
    error300: [240, 102, 106],
    warning: [244, 161, 51],
    success: [115, 175, 85],
    info: [230, 230, 230],
    white: [255, 255, 255],
    black: [0, 0, 0],
    imdb: [230, 185, 30],
    facebook: [0, 79, 244],
    backdrop: [23, 23, 23]
  },
  Blockbuster: {
    cta: [255, 225, 63],
    cta200: [255, 245, 192],
    cta300: [255, 238, 149],
    cta400: [255, 232, 106],
    cta500: [255, 225, 63],
    cta600: [221, 193, 44],
    ui100: [248, 250, 255],
    ui200: [248, 250, 255],
    ui300: [248, 250, 255],
    ui400: [24, 42, 104],
    ui500: [21, 36, 87],
    ui600: [20, 33, 77],
    ui700: [14, 25, 60],
    ui800: [12, 17, 49],
    ui900: [10, 10, 39],
    error: [235, 51, 56],
    error300: [240, 102, 106],
    warning: [244, 161, 51],
    success: [115, 175, 85],
    info: [230, 230, 230],
    white: [255, 255, 255],
    black: [0, 0, 0],
    imdb: [230, 185, 30],
    facebook: [0, 79, 244],
    backdrop: [0, 27, 159]
  }
};

const PALETTE: Record<ColorKey, RGB> =
  PALETTES[brand as 'SFAnytime' | 'Blockbuster'] ?? PALETTES.SFAnytime;

export const themeColor = (key: ColorKey, opacity?: number): string => {
  const [r, g, b] = PALETTE[key];
  return opacity === undefined
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const colors = COLOR_KEYS.reduce(
  (acc, k) => ({ ...acc, [k]: themeColor(k) }),
  {} as { [K in ColorKey]: string }
);

export type Theme = {
  color: { [K in ColorKey]: string };
  font: { roboto: string; robotoDin: string };
  size: {
    super: string;
    large: string;
    title: string;
    text: string;
    tinyTitle: string;
    infoText: string;
    tinyInfo: string;
  };
  weight: {
    bold: number;
    medium: number;
    regular: number;
    light: number;
  };
  letterSpacing: { big: string; small: string };
  lineHeight: { small: number };
  state: { disabledOpacity: number };
};

export const darkTheme: Theme = {
  color: colors,
  font: {
    roboto: 'Roboto, sans-serif',
    robotoDin: 'RobotoDin, sans-serif'
  },
  size: {
    super: '2.5rem',
    large: '1.5rem',
    title: '1.25rem',
    text: '1rem',
    tinyTitle: '0.8125rem',
    infoText: '0.8125rem',
    tinyInfo: '0.625rem'
  },
  weight: {
    bold: 700,
    medium: 500,
    regular: 400,
    light: 300
  },
  letterSpacing: { big: '1px', small: '0' },
  lineHeight: { small: 1.25 },
  state: { disabledOpacity: 0.5 }
};

const ThemeContext = React.createContext<Theme>(darkTheme);

export const ThemeProvider: React.FC<{
  theme?: Theme;
  children?: React.ReactNode;
}> = ({ theme = darkTheme, children }) =>
  React.createElement(ThemeContext.Provider, { value: theme }, children);

export const useTheme = (): { theme: Theme } => ({
  theme: React.useContext(ThemeContext)
});
